package app.smartcoding.realrate

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.material3.ExperimentalMaterial3ExpressiveApi
import androidx.compose.material3.MaterialExpressiveTheme
import androidx.compose.material3.Typography
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.retain.retain
import androidx.compose.ui.text.font.FontFamily
import androidx.lifecycle.viewmodel.compose.viewModel
import app.smartcoding.realrate.domain.model.LanguageCode
import app.smartcoding.realrate.localization.EnStrings
import app.smartcoding.realrate.localization.LocalStrings
import app.smartcoding.realrate.localization.strings
import app.smartcoding.realrate.platform.GatherConsentAndInitialize
import app.smartcoding.realrate.platform.getColorScheme
import app.smartcoding.realrate.platform.getSystemLanguage
import app.smartcoding.realrate.presentation.AuthViewModel
import app.smartcoding.realrate.presentation.CreditViewModel
import app.smartcoding.realrate.presentation.SettingsViewModel
import app.smartcoding.realrate.presentation.components.CreditAnalyzer
import app.smartcoding.realrate.presentation.util.fontFamilyForLanguage
import cafe.adriel.lyricist.ProvideStrings
import cafe.adriel.lyricist.rememberStrings

var isDebugMode: Boolean = false
    private set

@OptIn(ExperimentalMaterial3ExpressiveApi::class)
@Composable
fun App(debug: Boolean = false) {
    isDebugMode = debug
    val isSystemInDarkTheme = isSystemInDarkTheme()
    val defaultStrings = strings[getSystemLanguage()] ?: EnStrings
    val authViewModel = viewModel { AuthViewModel() }

    val authUser by retain {
        derivedStateOf { authViewModel.authUser }
    }

    val settingsViewModel = viewModel {
        SettingsViewModel(authUser, isSystemInDarkTheme, LanguageCode(defaultStrings.code))
    }

    LaunchedEffect(authUser) {
        settingsViewModel.setAuthUser(authUser)
    }

    val settings = settingsViewModel.settings

    val creditViewModel = viewModel { CreditViewModel() }

    remember(isSystemInDarkTheme) {
        settingsViewModel.onSystemThemeChanged(isSystemInDarkTheme)
    }

    GatherConsentAndInitialize { settingsViewModel.showAds(true) }

    val family: FontFamily = fontFamilyForLanguage(settings.languageCode)

    val typography = Typography().run {
        copy(
            displayLarge = displayLarge.copy(fontFamily = family),
            displayMedium = displayMedium.copy(fontFamily = family),
            displaySmall = displaySmall.copy(fontFamily = family),
            headlineLarge = headlineLarge.copy(fontFamily = family),
            headlineMedium = headlineMedium.copy(fontFamily = family),
            headlineSmall = headlineSmall.copy(fontFamily = family),
            titleLarge = titleLarge.copy(fontFamily = family),
            titleMedium = titleMedium.copy(fontFamily = family),
            titleSmall = titleSmall.copy(fontFamily = family),
            bodyLarge = bodyLarge.copy(fontFamily = family),
            bodyMedium = bodyMedium.copy(fontFamily = family),
            bodySmall = bodySmall.copy(fontFamily = family),
            labelLarge = labelLarge.copy(fontFamily = family),
            labelMedium = labelMedium.copy(fontFamily = family),
            labelSmall = labelSmall.copy(fontFamily = family)
        )
    }

    val lyricist = rememberStrings(
        translations = strings,
        defaultLanguageTag = settings.languageCode.code,
        currentLanguageTag = settings.languageCode.code,
    )

    ProvideStrings(lyricist, LocalStrings) {
        MaterialExpressiveTheme(
            colorScheme = getColorScheme(settingsViewModel.isDarkTheme),
            typography = typography,
            content = {
                CreditAnalyzer(
                    creditViewModel = creditViewModel,
                    authViewModel = authViewModel,
                    settingsViewModel = settingsViewModel
                )
            }
        )
    }
}
